import React, { useState, useCallback, useMemo } from 'react';
import useFetch from 'core/hooks/api/useFetch';
import usePut from 'core/hooks/api/usePut';
import usePost from 'core/hooks/api/usePost';
import useDelete from 'core/hooks/api/useDelete';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditFaqModal from 'components/admin/common/modal/EditFaqModal';
import { validateForm } from 'core/utils/validation';
import { Alert } from 'react-bootstrap';

const UserRegister = () => {
    const { data: apiResponse, loading, error, refetch } = useFetch(API_ENDPOINTS.CONTACT);
    const data = apiResponse?.data || [];
    const { putData: updateUserRegisterData, loading: updating, error: updateError } = usePut(API_ENDPOINTS.CONTACT);
    const { postData: createUserRegisterData, loading: creating, error: createError } = usePost(API_ENDPOINTS.CONTACT);
    const { deleteData: deleteUserRegisterData, loading: deleting, error: deleteError } = useDelete(API_ENDPOINTS.CONTACT);
    const [selectedUserRegister, setSelectedUserRegister] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userRegisterToDelete, setUserRegisterToDelete] = useState(null);
    const [errors, setErrors] = useState({});
    const [updateSuccess, setUpdateSuccess] = useState(false);
    const [modalSuccess, setModalSuccess] = useState(false);

    const validationRules = [
        { field: 'name', method: 'required' },
        { field: 'email', method: 'required' },
        { field: 'phone', method: 'required' },
        { field: 'user_type', method: 'required' },
        { field: 'message', method: 'required' },
        { field: 'product_ids', method: 'required' },
        { field: 'status', method: 'required' }
    ];

    const handleEditClick = useCallback((userRegister) => {
        setSelectedUserRegister(userRegister);
        setShowModal(true);
        setUpdateSuccess(false);
    }, []);

    const handleModalClose = useCallback(() => {
        setShowModal(false);
        setUpdateSuccess(false);
        setTimeout(() => {
            setSelectedUserRegister(null);
            setErrors({});
        }, 300);
    }, []);

    const handleModalSave = useCallback(async (updatedUserRegister) => {
        const formErrors = validateForm(updatedUserRegister, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }
        if (updatedUserRegister.id) {
            await updateUserRegisterData(updatedUserRegister.id, updatedUserRegister);
        } else {
            await createUserRegisterData(updatedUserRegister);
            refetch();
        }
        setShowModal(false);
        setSelectedUserRegister(null);
        setModalSuccess(true);
    }, [updateUserRegisterData, createUserRegisterData, refetch]);

    const handleCreateClick = useCallback(() => {
        setUpdateSuccess(false);
        setModalSuccess(false);
        setErrors({});
        const newUserRegister = {
            name: '',
            email: ''
        };
        setSelectedUserRegister(newUserRegister);
        setShowModal(true);
    }, []);

    const handleDeleteClick = useCallback((userRegister) => {
        setUserRegisterToDelete(userRegister);
        setShowDeleteModal(true);
        setUpdateSuccess(false);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (userRegisterToDelete) {
            await deleteUserRegisterData(userRegisterToDelete.id);
            refetch();
            setShowDeleteModal(false);
            setUpdateSuccess(false);
            setUserRegisterToDelete(null);
            setModalSuccess(true);
        }
    }, [userRegisterToDelete, deleteUserRegisterData, refetch]);

    const handleDeleteModalClose = useCallback(() => {
        setShowDeleteModal(false);
        setUpdateSuccess(false);
        setUserRegisterToDelete(null);
        setErrors({});
    }, []);

    const columns = useMemo(() => [
        { field: 'name', header: 'Name' },
        { field: 'email', header: 'Email' },
        { field: 'phone', header: 'Phone' },
        { field: 'user_type', header: 'User Type' },
        { field: 'message', header: 'Message' },
        { field: 'product_ids', header: 'Product IDs' },
        { field: 'status', header: 'Status' }
    ], []);

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                    <h5 className="m-0 text-title">User Register</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Add User
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {updateSuccess && <Alert variant="success">User updated successfully.</Alert>}
            {modalSuccess && <Alert variant="success">Operation completed successfully.</Alert>}
            {updateError && <Alert variant="danger">Error updating: {updateError.message}</Alert>}
            {createError && <Alert variant="danger">Error creating: {createError.message}</Alert>}
            {deleteError && <Alert variant="danger">Error deleting: {deleteError.message}</Alert>}
            <DataTableWrapper
                data={data}
                columns={columns}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                paginator={false}
                totalRecords={data.length}
                emptyMessage="No records found."
                sortField="name"
                sortOrder={1}
                lazy={true}
                showGridlines={true}
            />
            {selectedUserRegister && (
                <EditFaqModal
                    show={showModal}
                    faq={selectedUserRegister}
                    handleInputChange={() => {}}
                    handleClose={handleModalClose}
                    handleSave={handleModalSave}
                    updating={updating || creating}
                    errors={errors}
                />
            )}
            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={handleDeleteModalClose}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
            />
        </div>
    );
};

export default React.memo(UserRegister);